import {
  getCoreRowModel,
  useReactTable,
  flexRender,
  getSortedRowModel
} from "@tanstack/react-table";
import { ArrowIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import { t } from "i18next";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import { ItemsTableProps } from "./ItemsTable.types";

import styles from "./ItemsTable.module.scss";

const DataTable = <TData, TValue>({
  className,
  columns,
  data,
  sorting,
  isLoading,
  setSorting,
  onRowClick,
  specificRowsIndexes = [],
  specificColumnsIndexes = [],
  specificColumnsClassName = "",
  emptyListMessage = "There are no items matching your choice",
  skeletonHeight = 65,
  rowsClassName = "",
  headerRowClassName = "",
  specificRowsClassName = "",
  getLinkUrl,
  linkState,
  linkTarget
}: ItemsTableProps<TData, TValue>) => {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    onSortingChange: setSorting,
    state: { sorting }
  });

  return (
    <div className={styles.container}>
      <div className={cn(styles.table, className)}>
        <div className={styles.table_header}>
          {table.getHeaderGroups().map((headerGroup) => (
            <div
              key={headerGroup.id}
              className={cn(styles.table_headerRow, headerRowClassName)}
            >
              {headerGroup.headers.map((header, index) => (
                <div
                  key={header.id}
                  className={cn(styles.table_headerCell, {
                    [styles.table_headerCell__sorted]:
                      header.column.getIsSorted(),
                    [styles.table_headerCell__sorting]:
                      header.column.getCanSort()
                  })}
                  style={{ width: `${header.getSize()}%` }}
                >
                  <div
                    className={cn(styles.table_headerCell_content, {
                      [specificColumnsClassName]:
                        specificColumnsIndexes.includes(index)
                    })}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>

                    <If condition={header.column.getCanSort()}>
                      <ArrowIcon
                        width={18}
                        height={18}
                        className={cn(styles.table_headerCell_sortArrow, {
                          [styles.table_headerCell_sortArrow__rotated]:
                            header.column.getIsSorted() === "asc"
                        })}
                      />
                    </If>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className={styles.table_body}>
          {table.getRowModel().rows.map((row, index) =>
            getLinkUrl ? (
              <Link
                key={row.id}
                className={cn(styles.table_dataRow, rowsClassName, {
                  [styles.table_dataRow__clickable]: onRowClick ?? getLinkUrl,
                  [specificRowsClassName]: specificRowsIndexes.includes(index)
                })}
                onClick={(e) => {
                  if (onRowClick) {
                    e.preventDefault();
                    onRowClick(row.original);
                  }
                }}
                to={getLinkUrl?.(row.original) ?? ""}
                target={linkTarget}
                state={linkState}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className={styles.table_dataCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </Link>
            ) : (
              <div
                key={row.id}
                className={cn(styles.table_dataRow, rowsClassName, {
                  [styles.table_dataRow__clickable]: !!onRowClick,
                  [specificRowsClassName]: specificRowsIndexes.includes(index)
                })}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className={styles.table_dataCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      <If condition={isLoading}>
        <Skeleton
          height={skeletonHeight}
          className={styles.skeleton}
          count={5}
        />
      </If>

      <If condition={data.length === 0 && !isLoading}>
        <div className={styles.emptyDataMessage}>{t(emptyListMessage)}</div>
      </If>
    </div>
  );
};

export default DataTable;
